import type { LexiconReportContext } from '@orborix/shared-types';
/**
 * Cliente HTTP de Biblioteca (`/api/modules/english/lexicon*`, fase 20.B).
 *
 * Mapea el DTO del backend (`LexiconEntryView` / cobertura por tier) a la forma
 * que consumen los componentes del catálogo (espejo del shape del diseño:
 * `{ text, tr, ipa, pos, cefr, rank, kind, used, saved, … }`). Mismo patrón
 * delgado que `vocabApi.ts` (cookie httpOnly same-origin).
 */
import type { CefrLevel, VocabEntryType } from '../screens/vocabulary/vocabData.js';
import type { WordLookupEntry } from '../components/word-lookup/index.js';
import { createTypedApiClient } from '@orborix/auth-client';

const BASE = '/api/modules/english';

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'LexiconApiError',
  abortError: 'wrap',
});

export const LexiconApiError = client.ApiError;
export type LexiconApiError = InstanceType<typeof client.ApiError>;

/**
 * JSON **crudo**: estos endpoints devuelven el envelope y son los call sites
 * los que lo abren (`fetchLexicon` y `searchLexiconWords` leen `data.entries` /
 * `data.words` a mano). Por eso no usa el `get` del cliente compartido, que
 * desenvolvería el `{ data }` y les sacaría de las manos lo que van a leer.
 */
async function jsonFetch<T>(path: string): Promise<T> {
  const res = await client.request(path);
  await client.ensureOk(res);
  return (await res.json()) as T;
}

// ─── DTO del backend ────────────────────────────────────────────────
/**
 * Un significado del diccionario nuevo (v2, misión 50 del catálogo léxico).
 * `id` NO es posicional: el quinto significado de `run` es `run-4` y sigue
 * siéndolo aunque se reordenen (no se calcula con el índice).
 */
interface LexiconMeaningDto {
  id: string;
  /** Tipo canónico en inglés (`noun`, `verb`, `abbreviation`…). */
  pos: string;
  cefr: CefrLevel;
  translation: string;
  /** Otras traducciones de ESTE significado («También:»). */
  alts: string[];
  definition: string;
  example: string;
  exampleEs: string;
  /** Su pronunciación, sólo si se dice distinto que la palabra (un heterónimo: `live` «en vivo»). */
  ipa?: string;
}

interface LexiconEntryDto {
  reportContext?: LexiconReportContext;
  text: string;
  textNorm: string;
  pos: string | null;
  cefr: CefrLevel | null;
  freqRank: number | null;
  freqBand: number | null;
  kind: string;
  ipa: string | null;
  translation: string | null;
  senses: string[] | null;
  definition: string | null;
  example: string | null;
  verbForms: VerbForms | null;
  phrasal: PhrasalMeta | null;
  used: boolean;
  said: boolean;
  manual: boolean;
  saved: boolean;
  /**
   * Los significados, el principal primero. Null con el diccionario de hoy
   * (v1) y en las fichas generadas por el audiolibro. Con v2, los campos de
   * arriba (`pos`, `cefr`, `translation`, `definition`, `example`) son los del
   * principal y `senses` trae una traducción por significado.
   */
  meanings: LexiconMeaningDto[] | null;
  /** Traducción del ejemplo del principal (v2); null con v1. */
  exampleEs: string | null;
}

/** Una fila del buscador: qué significados encontró la búsqueda en español. */
interface LexiconSearchWordDto extends LexiconEntryDto {
  /** Ids de los significados cuya traducción (o alternativa) contiene `q`, en su orden; `[]` si coincidió el inglés o con v1. */
  matchedMeaningIds: string[];
}

/** Qué diccionario sirve el backend (`module_config.languages.lexiconDictionary`). */
export type LexiconDictionary = 'v1' | 'v2';

interface DictionaryEntryDto {
  reportContext?: LexiconReportContext;
  dictionary: LexiconDictionary;
  entry: LexiconEntryDto | null;
}

/** Conjugación de un verbo (sección Verbos). Espejo de `LexiconVerbForms` del backend. */
export interface VerbForms {
  base: string;
  thirdPerson: string;
  past: string;
  pastParticiple: string;
  gerund: string;
  irregular: boolean;
}

/** Metadata de phrasal verb (sección Phrasal). Espejo de `LexiconPhrasal` del backend. */
export interface PhrasalMeta {
  base: string;
  particle: string;
  separable: boolean;
  transitive: boolean;
}

interface LexiconListDto {
  entries: LexiconEntryDto[];
  nextCursor: number | null;
}

interface CoverageBandDto {
  band: number;
  label: string;
  corpusCoverage: number;
  blurbEs: string;
  known: number;
  said: number;
  marked: number;
  total: number;
  pct: number;
}

interface CoverageStarterDto {
  label: string;
  known: number;
  said: number;
  marked: number;
  total: number;
  pct: number;
}

interface CoverageDto {
  global: { known: number; said: number; marked: number; total: number; pct: number };
  bands: CoverageBandDto[];
  /** Sólo para un alumno A0 (plan 48 F2): la frontera «Primeros pasos» (lista `starter-1`). */
  starter?: CoverageStarterDto;
}

// ─── Shape de la UI (espejo del diseño) ─────────────────────────────
/** Un significado del diccionario nuevo (v2), con el tipo ya como etiqueta en español. */
export interface LexiconMeaning {
  id: string;
  /** Etiqueta gramatical en español (la misma de `pos`: sust./verbo/abreviatura…). */
  pos: string;
  cefr: CefrLevel;
  tr: string;
  alts: string[];
  definition: string;
  example: string;
  exampleEs: string;
  /**
   * Su pronunciación, SÓLO si se dice distinto que la palabra (heterónimos,
   * decisión 79 del catálogo léxico: `live` «en vivo» /laɪv/). Sin el campo,
   * el significado se dice como la palabra (`LexiconWord.ipa`).
   */
  ipa?: string;
}

export interface LexiconWord {
  reportContext?: LexiconReportContext;
  /** id estable para React (textNorm + pos). */
  id: string;
  text: string;
  /** traducción ES principal (para la lista). */
  tr: string;
  /** Todas las acepciones ES (para el detalle tipo diccionario). */
  senses: string[];
  /** Definición EN principal (glosa Wiktionary). Null si no hay cobertura. */
  definition: string | null;
  ipa: string | null;
  /** Etiqueta gramatical en español abreviado (sust./verbo/adj…). */
  pos: string | null;
  cefr: CefrLevel | null;
  rank: number;
  band: number;
  kind: VocabEntryType | 'word';
  topic: string | null;
  example: string | null;
  used: boolean;
  /** La PRODUJO (dicha en charla o Decila) — distingue "la dijiste" de "la marcaste". */
  said: boolean;
  /** La marcó "dominada" a mano (permite des-marcar desde la UI). */
  manual: boolean;
  saved: boolean;
  /** Reservado (vista en el Programa). Por ahora false. */
  mastered: boolean;
  /** Conjugación (sólo verbos). Null para no-verbos. */
  verbForms: VerbForms | null;
  /** Metadata de phrasal verb (sólo kind='phrasal_verb'). Null para el resto. */
  phrasal: PhrasalMeta | null;
  /** Significados del diccionario nuevo (v2), el principal primero. Null con v1. */
  meanings: LexiconMeaning[] | null;
  /** Traducción del ejemplo del principal (v2). Null con v1. */
  exampleEs: string | null;
  /** El significado que encontró el buscador en español (el primero); null fuera del buscador. */
  matchedMeaningId: string | null;
}

/**
 * POS canónico del catálogo (NGSL) → etiqueta corta en español. Los que trajo
 * el diccionario nuevo van con el nombre entero (decidido en la carga, #409).
 * Es lo que speak GUARDA al guardar una palabra (`partOfSpeech: w.pos`): la
 * ficha y Decila la escriben entera con `LIB_POS` de libData.ts, sólo al
 * dibujarla.
 */
export const POS_ES: Readonly<Record<string, string>> = {
  noun: 'sust.',
  verb: 'verbo',
  adjective: 'adj.',
  adverb: 'adv.',
  pronoun: 'pron.',
  preposition: 'prep.',
  conjunction: 'conj.',
  article: 'art.',
  determiner: 'det.',
  interjection: 'interj.',
  number: 'núm.',
  abbreviation: 'abreviatura',
  phrase: 'locución',
  prefix: 'prefijo',
  contraction: 'contracción',
  symbol: 'símbolo',
  particle: 'partícula',
  unknown: '',
};

function posLabel(pos: string | null): string | null {
  if (!pos) return null;
  return POS_ES[pos] ?? pos;
}

const KNOWN_KINDS: ReadonlySet<string> = new Set([
  'word',
  'phrase',
  'phrasal_verb',
  'expression',
  'collocation',
]);

const RANK_FALLBACK = 99999;

function toMeaning(m: LexiconMeaningDto): LexiconMeaning {
  const pos = posLabel(m.pos);
  if (!pos) throw new LexiconApiError(`El significado ${m.id} llegó sin tipo`, 500, 'bad_lexicon_payload');
  return {
    id: m.id,
    pos,
    cefr: m.cefr,
    tr: m.translation,
    alts: m.alts,
    definition: m.definition,
    example: m.example,
    exampleEs: m.exampleEs,
    ...(m.ipa ? { ipa: m.ipa } : {}),
  };
}

function toWord(d: LexiconEntryDto): LexiconWord {
  // Los dos campos de la carga viajan SIEMPRE (null con v1): si faltan, el
  // backend no es el de la carga y la ficha mostraría la de hoy sin avisar.
  if (d.meanings === undefined || d.exampleEs === undefined) {
    throw new LexiconApiError(`«${d.text}» llegó sin meanings/exampleEs`, 500, 'bad_lexicon_payload');
  }
  return {
    id: d.pos ? `${d.textNorm}|${d.pos}` : d.textNorm,
    text: d.text,
    tr: d.translation ?? '',
    senses: d.senses ?? [],
    definition: d.definition,
    ipa: d.ipa,
    pos: posLabel(d.pos),
    cefr: d.cefr,
    rank: d.freqRank ?? RANK_FALLBACK,
    band: d.freqBand ?? RANK_FALLBACK,
    kind: (KNOWN_KINDS.has(d.kind) ? d.kind : 'word') as VocabEntryType | 'word',
    topic: null,
    example: d.example,
    used: d.used,
    said: d.said,
    manual: d.manual,
    saved: d.saved,
    mastered: false,
    verbForms: d.verbForms,
    phrasal: d.phrasal,
    ...(d.reportContext ? { reportContext: d.reportContext } : {}),
    meanings: d.meanings ? d.meanings.map(toMeaning) : null,
    exampleEs: d.exampleEs,
    matchedMeaningId: null,
  };
}

// ─── Filtros server-side ────────────────────────────────────────────
export interface LexiconQuery {
  band?: number;
  /** Lista de palabras (plan 48 F2): `starter-1` = «Primeros pasos», la frontera del A0. */
  set?: LexiconSet;
  /** CEFR como string (el server valida el enum). */
  cefr?: string;
  pos?: string;
  kind?: string;
  irregular?: boolean;
  state?: 'used' | 'unused' | 'saved';
  search?: string;
  cursor?: number;
  limit?: number;
}

export interface LexiconPage {
  words: LexiconWord[];
  nextCursor: number | null;
}

export async function fetchLexicon(q: LexiconQuery = {}): Promise<LexiconPage> {
  const params = new URLSearchParams();
  if (q.band != null) params.set('band', String(q.band));
  if (q.set) params.set('set', q.set);
  if (q.cefr) params.set('cefr', q.cefr);
  if (q.pos) params.set('pos', q.pos);
  if (q.kind) params.set('kind', q.kind);
  if (q.irregular != null) params.set('irregular', String(q.irregular));
  if (q.state) params.set('state', q.state);
  if (q.search) params.set('search', q.search);
  if (q.cursor != null) params.set('cursor', String(q.cursor));
  if (q.limit != null) params.set('limit', String(q.limit));
  const qs = params.toString();
  const dto = await jsonFetch<LexiconListDto>(`/lexicon${qs ? `?${qs}` : ''}`);
  return { words: dto.entries.map(toWord), nextCursor: dto.nextCursor };
}

// ─── Búsqueda global (20.H, H3) — EN prefijo/substring + ES en translation ──
/**
 * Busca en el catálogo vía `GET /lexicon/search` (palabras EN+ES). Devuelve el
 * mismo shape que el listado. Las frases no-lexicón del user viajan en el
 * payload pero el buscador las resuelve client-side desde la colección cargada
 * (tiene más metadata: familiaridad, badges).
 *
 * Con el diccionario nuevo (v2) el español busca en las traducciones de todos
 * los significados: cada fila trae en `matchedMeaningId` el primero que
 * coincidió («dirigir» → el de `run` que dice «dirigir»), que es el que
 * muestra la fila y en el que abre la ficha.
 */
export async function searchLexiconWords(q: string, limit = 30): Promise<LexiconWord[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const dto = await jsonFetch<{
    success: boolean;
    data: { entries?: LexiconSearchWordDto[]; words?: LexiconSearchWordDto[] };
  }>(`/lexicon/search?${params.toString()}`);
  const words = dto.data.words ?? dto.data.entries;
  if (!words) throw new LexiconApiError('Respuesta de búsqueda sin words', 500, 'bad_search_payload');
  return words.map((d) => {
    if (!Array.isArray(d.matchedMeaningIds)) {
      throw new LexiconApiError(`«${d.text}» llegó sin matchedMeaningIds`, 500, 'bad_search_payload');
    }
    const w = toWord(d);
    // Sin coincidencia en un significado (la búsqueda dio con el inglés, o v1) → null.
    const matched = d.matchedMeaningIds[0] ?? null;
    if (matched !== null && !w.meanings?.some((m) => m.id === matched)) {
      throw new LexiconApiError(`«${d.text}»: el significado ${matched} no está entre los suyos`, 500, 'bad_search_payload');
    }
    return { ...w, matchedMeaningId: matched };
  });
}

// ─── La ficha de una palabra en el diccionario activo (Lo mío, decisión 73 f) ──
export interface DictionaryEntry {
  reportContext?: LexiconReportContext;
  dictionary: LexiconDictionary;
  /** Null = la palabra no está en el diccionario activo (salió, o es una frase). */
  entry: LexiconWord | null;
}

/**
 * Lee la ficha de UNA palabra en el diccionario que sirve el backend
 * (`GET /lexicon/entry?word=`). Lo usa Lo mío para abrir la ficha completa en
 * vez de la copia guardada. `entry: null` es "no está"; un error HTTP, de red
 * o un payload sin forma TIRA: nunca se confunde con una palabra ausente.
 */
export async function fetchDictionaryEntry(word: string): Promise<DictionaryEntry> {
  const params = new URLSearchParams({ word });
  const dto = await jsonFetch<{ success?: boolean; data?: Partial<DictionaryEntryDto> }>(
    `/lexicon/entry?${params.toString()}`,
  );
  const data = dto.data;
  if (!data || (data.dictionary !== 'v1' && data.dictionary !== 'v2') || data.entry === undefined) {
    throw new LexiconApiError('Respuesta de /lexicon/entry sin dictionary/entry', 500, 'bad_entry_payload');
  }
  return { dictionary: data.dictionary, entry: data.entry ? toWord(data.entry) : null, ...(data.reportContext ? { reportContext: data.reportContext } : {}) };
}

// ─── Cobertura ──────────────────────────────────────────────────────
export interface CoverageBand {
  band: number;
  label: string;
  corpusCoverage: number;
  blurbEs: string;
  known: number;
  /** De `known`, las producidas (charla/Decila). */
  said: number;
  /** De `known`, las auto-reportadas ("ya la sé"). */
  marked: number;
  total: number;
  pct: number;
}

/** Las listas que acepta `GET /lexicon?set=` (hoy una sola). */
export type LexiconSet = 'starter-1';

/**
 * La frontera del A0 (plan 48 F2): cuántas de las ~285 palabras de
 * `starter-1` — lo que el coach le dice a un A0 — ya conoce. Mismo criterio
 * known/said/marked que las bandas. Su PRESENCIA es el gate: el backend la
 * manda sólo a un alumno A0, así que el front no mira el nivel.
 */
export interface CoverageStarter {
  label: string;
  known: number;
  said: number;
  marked: number;
  total: number;
  pct: number;
}

export interface Coverage {
  global: { known: number; said: number; marked: number; total: number; pct: number };
  bands: CoverageBand[];
  starter?: CoverageStarter;
}

const STARTER_NUMS = ['known', 'said', 'marked', 'total', 'pct'] as const;

/**
 * Valida `starter` en el borde: si viene, tiene que venir entero (nunca un
 * medidor a medias). Sin `starter` (A1+) la respuesta pasa tal cual, como antes.
 */
function assertCoverageStarter(dto: CoverageDto): void {
  const s: unknown = dto.starter;
  if (s === undefined) return;
  const ok =
    typeof s === 'object' &&
    s !== null &&
    typeof (s as CoverageStarterDto).label === 'string' &&
    (s as CoverageStarterDto).label.length > 0 &&
    STARTER_NUMS.every((k) => Number.isFinite((s as Record<string, unknown>)[k]));
  if (!ok) throw new LexiconApiError('Cobertura con starter incompleto', 500, 'bad_coverage_payload');
}

export async function fetchCoverage(): Promise<Coverage> {
  const dto = await jsonFetch<CoverageDto>('/lexicon/coverage');
  assertCoverageStarter(dto);
  return dto;
}

// ─── Glosario de capítulo (tap-to-lookup del karaoke, Fase 19.F) ────────────
interface GlossaryEntryDto {
  word: string;
  translation: string;
  definition: string | null;
  partOfSpeech: string | null;
  examples: string[];
  ipa: string | null;
  cefr: CefrLevel | null;
  senses: string[];
  /** Requerido por el contrato de `/lexicon/batch` — ver `GlossaryEntry` del backend. */
  source: 'lexicon' | 'dictionary';
}

/**
 * Resuelve en batch las palabras de un capítulo (`POST /lexicon/batch`). El
 * backend lee lexicon-first (lo mismo que la Biblioteca) → diccionario → LLM
 * (genera+persiste faltantes). Se llama UNA vez al abrir el capítulo; el popover
 * del karaoke lee del mapa resultante → cero LLM al tocar una palabra.
 *
 * Las claves del mapa son la forma normalizada (`normalizeWord`); el `pos` se
 * mapea a la etiqueta ES corta (igual que la lista de la Biblioteca).
 */
export async function fetchGlossaryBatch(words: string[]): Promise<Record<string, WordLookupEntry>> {
  if (words.length === 0) return {};
  const res = await client.request('/lexicon/batch', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ words }),
  });
  await client.ensureOk(res);
  const json = (await res.json()) as { data?: { entries?: Record<string, GlossaryEntryDto> } };
  const entries = json.data?.entries ?? {};
  const out: Record<string, WordLookupEntry> = {};
  for (const [norm, e] of Object.entries(entries)) {
    out[norm] = {
      translation: e.translation,
      definition: e.definition,
      partOfSpeech: posLabel(e.partOfSpeech),
      examples: e.examples,
      ipa: e.ipa,
      cefr: e.cefr,
      senses: e.senses,
      source: e.source,
    };
  }
  return out;
}

/** Marca/desmarca una palabra como "dominada" a mano. */
export async function markWord(word: string, mastered: boolean): Promise<void> {
  const res = await client.request('/lexicon/mark', {
    method: 'POST',
    body: JSON.stringify({ word, mastered }),
  });
  await client.ensureOk(res);
}


/**
 * El DTO de una entrada en la forma de la Biblioteca. Lo usa también el
 * «significado de esa frase» (`contextMeaningApi.ts`), que trae la entrada en
 * su respuesta.
 */
export function lexiconWordFromDto(d: LexiconEntryDto): LexiconWord {
  return toWord(d);
}
export type { LexiconEntryDto };
